import { z } from 'zod';

import { idSchema, slugSchema } from './common.js';

/**
 * UFs brasileiras (26 estados + DF). Usado em projects.estadoUf e no mapa.
 */
export const ufSchema = z.enum([
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
]);
export type Uf = z.infer<typeof ufSchema>;

/**
 * Modalidades contratuais cobertas pelo IDPE (extraídas das copies).
 */
export const modalidadeSchema = z.enum([
  'concessao-administrativa',
  'concessao-plena',
  'concessao-patrocinada',
  'concessao-de-uso',
  'locacao-de-ativos',
  'programa-de-parcerias',
  'estudo-de-viabilidade',
  'outra',
]);
export type Modalidade = z.infer<typeof modalidadeSchema>;

/**
 * Status do projeto no ciclo IDPE.
 * Cores e ordem espelham a convenção visual usada nas copies.
 */
export const projectStatusSchema = z.enum([
  'estruturacao',
  'consulta-publica',
  'licitacao',
  'contrato-assinado',
  'operacao',
  'revisao',
]);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/**
 * Porte do município (regra IDPE registrada no IDPE_em_Numeros_Copy):
 * - pequeno: até 50 mil habitantes
 * - medio : 50 mil a 500 mil
 * - grande: acima de 500 mil
 */
export const porteSchema = z.enum(['pequeno', 'medio', 'grande']);
export type Porte = z.infer<typeof porteSchema>;

/**
 * Setores (6 frentes de atuação aprovadas pelo cliente).
 */
export const setorSlugSchema = z.enum([
  'infraestrutura',
  'desenvolvimento-urbano',
  'cidades-inteligentes',
  'infraestrutura-escolar',
  'centros-administrativos',
  'servicos-cemiteriais',
]);
export type SetorSlug = z.infer<typeof setorSlugSchema>;

/**
 * Labels pt-BR dos status e modalidades. Compartilhados entre API e UI
 * para garantir consistência.
 */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  estruturacao: 'Estruturação',
  'consulta-publica': 'Consulta pública',
  licitacao: 'Licitação',
  'contrato-assinado': 'Contrato assinado',
  operacao: 'Operação',
  revisao: 'Revisão contratual',
};

export const MODALIDADE_LABELS: Record<Modalidade, string> = {
  'concessao-administrativa': 'Concessão Administrativa',
  'concessao-plena': 'Concessão Plena',
  'concessao-patrocinada': 'Concessão Patrocinada',
  'concessao-de-uso': 'Concessão de Uso',
  'locacao-de-ativos': 'Locação de Ativos',
  'programa-de-parcerias': 'Programa de Parcerias',
  'estudo-de-viabilidade': 'Estudo de Viabilidade',
  outra: 'Outra',
};

export const PORTE_LABELS: Record<Porte, string> = {
  pequeno: 'Pequeno porte (até 50k hab.)',
  medio: 'Médio porte (50k a 500k)',
  grande: 'Grande porte (acima de 500k)',
};

export const SETOR_LABELS: Record<SetorSlug, string> = {
  infraestrutura: 'Infraestrutura',
  'desenvolvimento-urbano': 'Desenvolvimento urbano',
  'cidades-inteligentes': 'Cidades inteligentes',
  'infraestrutura-escolar': 'Infraestrutura escolar',
  'centros-administrativos': 'Centros administrativos',
  'servicos-cemiteriais': 'Serviços cemiteriais',
};

export const projectSchema = z.object({
  id: idSchema,
  slug: slugSchema,
  nome: z.string().min(1).max(200),
  municipio: z.string().min(1).max(120),
  estadoUf: ufSchema,
  modalidade: modalidadeSchema,
  escopo: z.string().max(2000).nullable(),
  /** CAPEX em centavos para evitar floating point. R$ 1,5 bi → 150_000_000_000. */
  capexCentavos: z.number().int().min(0).nullable(),
  ano: z.number().int().min(2000).max(2100).nullable(),
  status: projectStatusSchema,
  /** Slug do setor (FK para sectors.slug). */
  setorSlug: setorSlugSchema,
  porte: porteSchema,
  lat: z.number().min(-90).max(90).nullable(),
  lng: z.number().min(-180).max(180).nullable(),
  exibirHome: z.boolean(),
  exibirMapa: z.boolean(),
  descricao: z.string().nullable(),
  criadoEm: z.string().datetime({ offset: true }),
  atualizadoEm: z.string().datetime({ offset: true }),
});
export type Project = z.infer<typeof projectSchema>;

export const createProjectInputSchema = z.object({
  slug: slugSchema,
  nome: z.string().min(1).max(200),
  municipio: z.string().min(1).max(120),
  estadoUf: ufSchema,
  modalidade: modalidadeSchema,
  escopo: z.string().max(2000).optional().or(z.literal('')),
  capexCentavos: z.number().int().min(0).nullable().optional(),
  ano: z.number().int().min(2000).max(2100).nullable().optional(),
  status: projectStatusSchema,
  setorSlug: setorSlugSchema,
  porte: porteSchema,
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  exibirHome: z.boolean(),
  exibirMapa: z.boolean(),
  descricao: z.string().max(5000).optional().or(z.literal('')),
});
export type CreateProjectInput = z.infer<typeof createProjectInputSchema>;

export const updateProjectInputSchema = createProjectInputSchema.partial().omit({ slug: true });
export type UpdateProjectInput = z.infer<typeof updateProjectInputSchema>;

/**
 * Query da listagem. Suporta busca textual (nome+município), filtros
 * múltiplos (setor, status, porte, UF), paginação e sort.
 */
export const listProjectsQuerySchema = z.object({
  q: z.string().max(120).optional(),
  setor: setorSlugSchema.optional(),
  status: projectStatusSchema.optional(),
  porte: porteSchema.optional(),
  uf: ufSchema.optional(),
  exibirHome: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  exibirMapa: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  ano: z.coerce.number().int().min(2000).max(2100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().max(40).optional(),
});
export type ListProjectsQuery = z.infer<typeof listProjectsQuerySchema>;
